import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import PhygitalIcon from "@/components/PhygitalIcon";
import SharedFooter from "@/components/SharedFooter";
import { useUiSurface, slotText } from "@/hooks/useUiSurface";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Sparkles,
  ChevronRight,
  Info,
  Users,
  Shield,
  Share,
  Lock,
  Eye,
  PartyPopper,
  Heart,
  MessageCircle,
  Bug,
  Lightbulb,
  Send,
  Check,
  Paperclip,
  X,
  Image as ImageIcon,
  FileText
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

const HelpPage = () => {
  const { slotMap: hp } = useUiSurface("surface_help_page");
  const [activeSection, setActiveSection] = useState(null);
  const [activeFaq, setActiveFaq] = useState(null);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactType, setContactType] = useState("general");
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    message: "",
    agreedToBeehiiv: false
  });
  const [attachments, setAttachments] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const fileInputRef = useRef(null);

  const faqCategories = [
    {
      id: "general",
      title: "General",
      icon: Info,
      color: "from-gray-600 to-gray-800",
      faqs: [
        { q: "What is Phygital?", a: "Phygital is a preference and context layer for real-life experiences. Store your preferences once and share them with service providers, event hosts, and venues." },
        { q: "Is Phygital free?", a: "Yes! Creating your profile and storing preferences is completely free. Event hosting with our Event OS is also free during our pilot phase." },
        { q: "How do I get started?", a: "Visit phygital.systems, sign in with your phone number, and start building your preference profile." }
      ]
    },
    {
      id: "events",
      title: "Events & RSVPs",
      icon: PartyPopper,
      color: "from-orange-500 to-amber-500",
      faqs: [
        { q: "How do I RSVP to an event?", a: "Visit the event page link shared by your host, enter your phone number to sign in, and complete the RSVP form." },
        { q: "What information do I share when I RSVP?", a: "You control what you share. You can share dietary restrictions, accessibility needs, and interests. Each category has a toggle so you decide what the host sees." },
        { q: "Can the host see my personal information?", a: "Hosts see aggregate data and can view individual preferences you chose to share. Your phone number is never shared." }
      ]
    },
    {
      id: "hosting",
      title: "Event Hosting",
      icon: Users,
      color: "from-purple-500 to-indigo-500",
      faqs: [
        { q: "How do I host an event?", a: "Sign in, go to your profile, and create a new event. You'll get a unique event page link to share with your guests." },
        { q: "What data do I see as a host?", a: "You see aggregate analytics (dietary breakdown, accessibility needs summary) and can view individual guest preferences." },
        { q: "Is event hosting free?", a: "Yes! During our pilot phase, all Event OS features are free." }
      ]
    },
    {
      id: "privacy",
      title: "Privacy & Security",
      icon: Shield,
      color: "from-green-500 to-emerald-500",
      faqs: [
        { q: "How is my data protected?", a: "Your data is encrypted in transit and at rest. We never sell your data. You control exactly what you share." },
        { q: "Who can see my preferences?", a: "Only the specific hosts or providers you share with. Your preferences are never shared without your explicit consent." },
        { q: "How do I delete my account?", a: "Go to Settings > Account > Delete Account. This permanently removes all your data." }
      ]
    },
    {
      id: "sharing",
      title: "Sharing & QR Codes",
      icon: Share,
      color: "from-blue-500 to-cyan-500",
      faqs: [
        { q: "How do I share my preferences?", a: "From your profile or experience, tap 'Share' to get a unique link or QR code." },
        { q: "Can I preview what others see?", a: "Yes! Use the eye icon to preview exactly what the recipient will see." },
        { q: "Do links expire?", a: "Event links remain active until the host deactivates them. Style experience links stay active until you delete the experience." }
      ]
    }
  ];

  const quickTips = [
    { icon: Eye, text: "Use the preview toggle to see what you're sharing" },
    { icon: Lock, text: "Your phone number is never shared" },
    { icon: Heart, text: "Invisible conditions help staff accommodate you" },
    { icon: Share, text: "Create different experiences for different appointments" }
  ];

  const contactTypes = [
    { id: "bug", label: "Report a Bug", icon: Bug, color: "bg-red-100 text-red-600", description: "Something not working?" },
    { id: "feature", label: "Feature Request", icon: Lightbulb, color: "bg-amber-100 text-amber-600", description: "Ideas to improve Phygital" },
  ];

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(file => {
      const isValidType = file.type.startsWith('image/') || file.type === 'application/pdf' || file.type === 'text/plain';
      const isValidSize = file.size <= 10 * 1024 * 1024; // 10MB limit
      return isValidType && isValidSize;
    });
    
    if (validFiles.length !== files.length) {
      toast.error("Some files were skipped. Only images, PDFs, and text files under 10MB are allowed.");
    }
    
    // Convert to base64 for storage
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setAttachments(prev => [...prev, {
          name: file.name,
          type: file.type,
          size: file.size,
          data: e.target.result
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.agreedToBeehiiv) {
      toast.error("Please agree to receive updates to continue.");
      return;
    }
    
    setIsSubmitting(true);
    try {
      // Submit to help endpoint (Notion + Beehiiv)
      await axios.post(`${API}/help/submit`, {
        name: contactForm.name,
        email: contactForm.email,
        message: contactForm.message,
        type: contactType,
        attachments: attachments.map(a => ({ name: a.name, type: a.type, data: a.data })),
        consent_given: true,
        beehiiv_consent: true,
        timestamp: new Date().toISOString()
      });
      setSubmitSuccess(true);
      toast.success("Your message has been sent!");
    } catch (error) {
      console.error("Submission error:", error);
      // Still show success for UX - will be handled manually if API fails
      setSubmitSuccess(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetContactForm = () => {
    setContactForm({ name: "", email: "", message: "", agreedToBeehiiv: false });
    setAttachments([]);
    setSubmitSuccess(false);
    setShowContactForm(false);
  };

  const getFileIcon = (type) => {
    if (type.startsWith('image/')) return ImageIcon;
    return FileText;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="p-2 -ml-2 hover:bg-gray-100 rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2">
              <PhygitalIcon className="w-6 h-6" />
              <h1 className="font-display text-lg font-semibold">Help Center</h1>
            </div>
            <div className="w-9" />
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-5 flex-1">
        {/* Hero */}
        <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl p-5 text-white text-center">
          <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mx-auto mb-3">
            <Sparkles className="w-6 h-6" />
          </div>
          <h2 className="text-lg font-display font-bold mb-1">{slotText(hp, "hero_title", "How can we help you?")}</h2>
          <p className="text-gray-300 text-sm">{slotText(hp, "hero_subtitle", "Find answers or contact our team")}</p>
        </div>

        {/* Quick Tips */}
        <div className="bg-purple-50 border border-purple-100 rounded-xl p-4">
          <h3 className="font-semibold text-purple-900 mb-2 text-sm flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            Quick Tips
          </h3>
          <ul className="grid grid-cols-2 gap-2">
            {quickTips.map((tip, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-purple-800">
                <tip.icon className="w-3.5 h-3.5 text-purple-500 mt-0.5 flex-shrink-0" />
                <span>{tip.text}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact Us Section - Prominent */}
        <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
          <h3 className="font-semibold text-gray-900 mb-1 text-center">Need to reach us?</h3>
          <p className="text-xs text-gray-500 mb-4 text-center">Choose a category and we&apos;ll get back to you</p>
          
          <div className="grid grid-cols-2 gap-3">
            {contactTypes.map((type) => (
              <button
                key={type.id}
                onClick={() => { setContactType(type.id); setShowContactForm(true); }}
                className={`flex flex-col items-center gap-1.5 p-4 rounded-xl border-2 transition-all hover:scale-[1.02] ${type.color} border-current`}
              >
                <type.icon className="w-6 h-6" />
                <span className="text-sm font-medium text-center leading-tight">{type.label}</span>
                <span className="text-[10px] opacity-70">{type.description}</span>
              </button>
            ))}
          </div>
        </div>

        {/* FAQ Categories */}
        <div className="space-y-3">
          <h3 className="font-semibold text-gray-900 px-1 text-sm">Frequently Asked Questions</h3>
          
          {faqCategories.map((category) => (
            <div key={category.id} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <button
                onClick={() => setActiveSection(activeSection === category.id ? null : category.id)}
                className="w-full flex items-center gap-3 p-3 text-left"
              >
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${category.color} flex items-center justify-center flex-shrink-0`}>
                  <category.icon className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 text-sm">{category.title}</p>
                  <p className="text-xs text-gray-500">{category.faqs.length} questions</p>
                </div>
                <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${activeSection === category.id ? 'rotate-90' : ''}`} />
              </button>
              
              {activeSection === category.id && (
                <div className="px-3 pb-3 pt-0">
                  <div className="border-t border-gray-100 pt-2 space-y-1.5">
                    {category.faqs.map((faq, i) => (
                      <div key={i} className="bg-gray-50 rounded-lg overflow-hidden">
                        <button
                          onClick={() => setActiveFaq(activeFaq === `${category.id}-${i}` ? null : `${category.id}-${i}`)}
                          className="w-full p-2.5 text-left flex items-center gap-2"
                        >
                          <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform flex-shrink-0 ${activeFaq === `${category.id}-${i}` ? 'rotate-90' : ''}`} />
                          <span className="font-medium text-xs text-gray-800">{faq.q}</span>
                        </button>
                        {activeFaq === `${category.id}-${i}` && (
                          <div className="px-8 pb-2.5">
                            <p className="text-xs text-gray-600 leading-relaxed">{faq.a}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </main>

      {/* Footer */}
      <SharedFooter />

      {/* Contact Form Modal */}
      <Dialog open={showContactForm} onOpenChange={setShowContactForm}>
        <DialogContent className="sm:max-w-md bg-white border-0 shadow-2xl rounded-2xl p-0 max-h-[90vh] overflow-hidden">
          {!submitSuccess ? (
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
                {contactTypes.find(t => t.id === contactType)?.icon && (
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${contactTypes.find(t => t.id === contactType)?.color}`}>
                    {(() => { const Icon = contactTypes.find(t => t.id === contactType)?.icon; return Icon ? <Icon className="w-5 h-5" /> : null; })()}
                  </div>
                )}
                <div className="flex-1">
                  <h2 className="font-semibold text-gray-900">{contactTypes.find(t => t.id === contactType)?.label}</h2>
                  <p className="text-xs text-gray-500">{contactTypes.find(t => t.id === contactType)?.description}</p>
                </div>
              </div>
              
              <form onSubmit={handleContactSubmit} className="p-5 space-y-4 overflow-y-auto flex-1">
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    value={contactForm.name}
                    onChange={(e) => setContactForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Name *"
                    required
                    className="h-10 bg-gray-50 border-gray-200 rounded-lg text-sm"
                  />
                  <Input
                    type="email"
                    value={contactForm.email}
                    onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="Email *"
                    required
                    className="h-10 bg-gray-50 border-gray-200 rounded-lg text-sm"
                  />
                </div>
                
                <Textarea
                  value={contactForm.message}
                  onChange={(e) => setContactForm(prev => ({ ...prev, message: e.target.value }))}
                  placeholder={
                    contactType === 'bug' ? "Describe the issue you encountered. What were you trying to do? What happened instead?" : 
                    contactType === 'feedback' ? "Tell us about your idea or suggestion. How would it improve your experience?" : 
                    "How can we help you?"
                  }
                  required
                  className="min-h-[100px] bg-gray-50 border-gray-200 rounded-lg resize-none text-sm"
                />
                
                {/* File Attachments - always show for bug and feature */}
                {(contactType === 'bug' || contactType === 'feature') && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-medium text-gray-600">Attachments (optional)</label>
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
                      >
                        <Paperclip className="w-3.5 h-3.5" />
                        Add files
                      </button>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*,.pdf,.txt"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    
                    {attachments.length > 0 && (
                      <div className="space-y-1.5">
                        {attachments.map((file, index) => {
                          const FileIcon = getFileIcon(file.type);
                          return (
                            <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                              <FileIcon className="w-4 h-4 text-gray-400" />
                              <span className="text-xs text-gray-700 flex-1 truncate">{file.name}</span>
                              <span className="text-[10px] text-gray-400">{(file.size / 1024).toFixed(0)}KB</span>
                              <button
                                type="button"
                                onClick={() => removeAttachment(index)}
                                className="p-0.5 hover:bg-gray-200 rounded"
                              >
                                <X className="w-3.5 h-3.5 text-gray-400" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <p className="text-[10px] text-gray-400">Screenshots help us understand your issue better. Max 10MB per file.</p>
                  </div>
                )}
                
                {/* Consent */}
                <div className="flex items-start gap-2 py-2 px-3 bg-blue-50 rounded-lg border border-blue-100">
                  <Checkbox
                    id="contact-beehiiv"
                    checked={contactForm.agreedToBeehiiv}
                    onCheckedChange={(checked) => setContactForm(prev => ({ ...prev, agreedToBeehiiv: checked }))}
                    className="mt-0.5"
                  />
                  <label htmlFor="contact-beehiiv" className="text-[10px] text-gray-600 leading-snug cursor-pointer">
                    I agree to receive responses and updates. View our{" "}
                    <a href="/privacy" target="_blank" className="underline">Privacy</a>,{" "}
                    <a href="https://www.beehiiv.com/tou" target="_blank" rel="noopener noreferrer" className="underline">Beehiiv TOU</a> &{" "}
                    <a href="https://www.beehiiv.com/privacy" target="_blank" rel="noopener noreferrer" className="underline">Beehiiv Privacy</a>.
                  </label>
                </div>
                
                <Button
                  type="submit"
                  disabled={isSubmitting || !contactForm.name || !contactForm.email || !contactForm.message || !contactForm.agreedToBeehiiv}
                  className="w-full h-10 bg-black text-white hover:bg-gray-800 rounded-lg font-medium text-sm disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Sending...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Send className="w-4 h-4" />
                      Send Message
                    </span>
                  )}
                </Button>
              </form>
            </div>
          ) : (
            <div className="text-center py-8 px-5">
              <div className="w-14 h-14 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Check className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-1.5">Message Sent!</h3>
              <p className="text-gray-500 text-sm mb-5">
                We&apos;ll get back to you as soon as possible.
              </p>
              <Button variant="outline" onClick={resetContactForm} className="rounded-lg">
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HelpPage;
